import { useState, useMemo } from "react";
import { GlassCard } from "@/components/ui/glass-card";
import { type Device } from "@/hooks/useDevices";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { Smartphone, Network, HardDrive, Monitor, Signal, User, Info, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface SystemInformationProps {
  device: Device;
}

interface PropertyItem {
  section: string;
  sectionIcon: React.ReactNode;
  label: string;
  value: string;
}

const SystemInformation = ({ device }: SystemInformationProps) => {
  const [searchQuery, setSearchQuery] = useState('');

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const formatBytes = (bytes: number | null | undefined) => {
    if (!bytes) return 'Not Reported';
    return `${bytes.toFixed(2)} GB`;
  };

  const formatPercentage = (decimal: number | null | undefined) => {
    if (decimal === null || decimal === undefined) return null;
    return `${Math.round(decimal * 100)}%`;
  };

  const formatMacAddress = (mac: string | undefined) => {
    if (!mac) return 'Not Reported';
    return mac;
  };

  const formatBoolean = (value: boolean | undefined) => {
    if (value === undefined) return 'Not Reported';
    return value ? 'Yes' : 'No';
  };

  const additional = device.deviceAdditionalProperties;
  const isMac = device.os_type?.toLowerCase() === 'macos';
  const isIOS = device.os_type?.toLowerCase() === 'ios' || device.os_type?.toLowerCase() === 'ipados';

  // Build all properties for search
  const allProperties = useMemo<PropertyItem[]>(() => {
    const props: PropertyItem[] = [];
    const identityIcon = <Smartphone className="h-4 w-4 text-primary" />;
    const networkIcon = <Network className="h-4 w-4 text-primary" />;
    const hardwareIcon = <HardDrive className="h-4 w-4 text-primary" />;
    const systemIcon = <Monitor className="h-4 w-4 text-primary" />;
    const cellularIcon = <Signal className="h-4 w-4 text-primary" />;
    const userIcon = <User className="h-4 w-4 text-primary" />;
    const statusIcon = <Info className="h-4 w-4 text-primary" />;

    // Device Identity
    props.push({ section: 'Device Identity', sectionIcon: identityIcon, label: 'Device Name', value: device.name || 'Not Reported' });
    props.push({ section: 'Device Identity', sectionIcon: identityIcon, label: 'Hostname', value: device.hostname || 'Not Reported' });
    props.push({ section: 'Device Identity', sectionIcon: identityIcon, label: 'Serial Number', value: device.serial_number || 'Not Reported' });
    props.push({ section: 'Device Identity', sectionIcon: identityIcon, label: 'UDID', value: additional?.UDID || 'Not Reported' });
    props.push({ section: 'Device Identity', sectionIcon: identityIcon, label: 'Product', value: additional?.ProductName || 'Not Reported' });
    if (additional?.ModelNumber) {
      props.push({ section: 'Device Identity', sectionIcon: identityIcon, label: 'Model Number', value: additional.ModelNumber });
    }

    // Network Information
    props.push({ section: 'Network Information', sectionIcon: networkIcon, label: 'IP Address', value: device.ip_address || 'Not Reported' });
    props.push({ section: 'Network Information', sectionIcon: networkIcon, label: 'WiFi MAC Address', value: formatMacAddress(additional?.WiFiMAC) });
    props.push({ section: 'Network Information', sectionIcon: networkIcon, label: 'Bluetooth MAC Address', value: formatMacAddress(additional?.BluetoothMAC) });
    if (isMac && additional?.EthernetMAC) {
      props.push({ section: 'Network Information', sectionIcon: networkIcon, label: 'Ethernet MAC Address', value: formatMacAddress(additional.EthernetMAC) });
    }

    // Hardware & Storage
    props.push({ section: 'Hardware & Storage', sectionIcon: hardwareIcon, label: 'Battery Level', value: formatPercentage(additional?.BatteryLevel || device.battery_level) || 'Not Reported' });
    props.push({ section: 'Hardware & Storage', sectionIcon: hardwareIcon, label: 'Battery Status', value: device.battery_status || 'Not Reported' });
    props.push({ section: 'Hardware & Storage', sectionIcon: hardwareIcon, label: 'Total Capacity', value: formatBytes(additional?.DeviceCapacity) });
    props.push({ section: 'Hardware & Storage', sectionIcon: hardwareIcon, label: 'Available Capacity', value: formatBytes(additional?.AvailableDeviceCapacity) });
    if (additional?.HasBattery !== undefined) {
      props.push({ section: 'Hardware & Storage', sectionIcon: hardwareIcon, label: 'Has Battery', value: formatBoolean(additional.HasBattery) });
    }

    // System Details
    props.push({ section: 'System Details', sectionIcon: systemIcon, label: 'Operating System', value: device.os_type || 'Not Reported' });
    props.push({ section: 'System Details', sectionIcon: systemIcon, label: 'OS Version', value: device.os_version || 'Not Reported' });
    props.push({ section: 'System Details', sectionIcon: systemIcon, label: 'Build Version', value: additional?.BuildVersion || 'Not Reported' });
    if (additional?.SupplementalBuildVersion) {
      props.push({ section: 'System Details', sectionIcon: systemIcon, label: 'Supplemental Build', value: additional.SupplementalBuildVersion });
    }
    if (isIOS && additional?.TimeZone) {
      props.push({ section: 'System Details', sectionIcon: systemIcon, label: 'Time Zone', value: additional.TimeZone });
    }

    // Cellular Information (iOS only)
    if (isIOS && additional?.ServiceSubscriptions && additional.ServiceSubscriptions.length > 0) {
      additional.ServiceSubscriptions.forEach((sub, index) => {
        const slotName = sub.Slot?.replace('CTSubscriptionSlot', 'Slot ') || `Slot ${index + 1}`;
        if (sub.IMEI) props.push({ section: 'Cellular Information', sectionIcon: cellularIcon, label: `IMEI (${slotName})`, value: sub.IMEI });
        if (sub.MEID) props.push({ section: 'Cellular Information', sectionIcon: cellularIcon, label: `MEID (${slotName})`, value: sub.MEID });
        if (sub.EID) props.push({ section: 'Cellular Information', sectionIcon: cellularIcon, label: `EID (${slotName})`, value: sub.EID });
      });
      if (additional.ModemFirmwareVersion) {
        props.push({ section: 'Cellular Information', sectionIcon: cellularIcon, label: 'Modem Firmware', value: additional.ModemFirmwareVersion });
      }
    }

    // User Information (Mac only)
    if (isMac) {
      if (additional?.UserID) props.push({ section: 'User Information', sectionIcon: userIcon, label: 'User ID', value: additional.UserID });
      if (additional?.UserLongName) props.push({ section: 'User Information', sectionIcon: userIcon, label: 'User Name', value: additional.UserLongName });
      if (additional?.UserShortName) props.push({ section: 'User Information', sectionIcon: userIcon, label: 'Short Name', value: additional.UserShortName });
      if (additional?.NotOnConsole !== undefined) props.push({ section: 'User Information', sectionIcon: userIcon, label: 'On Console', value: formatBoolean(!additional.NotOnConsole) });
    }

    // General Information
    props.push({ section: 'General Information', sectionIcon: statusIcon, label: 'Last Seen', value: formatDate(device.last_seen || device.created_at) });
    props.push({ section: 'General Information', sectionIcon: statusIcon, label: 'Supervised', value: formatBoolean(additional?.IsSupervised) });
    if (isIOS) {
      props.push({ section: 'General Information', sectionIcon: statusIcon, label: 'Activation Lock', value: formatBoolean(additional?.IsActivationLockEnabled) });
      props.push({ section: 'General Information', sectionIcon: statusIcon, label: 'Cloud Backup', value: formatBoolean(additional?.IsCloudBackupEnabled) });
      props.push({ section: 'General Information', sectionIcon: statusIcon, label: 'Lost Mode', value: formatBoolean(additional?.IsMDMLostModeEnabled) });
      props.push({ section: 'General Information', sectionIcon: statusIcon, label: 'Data Roaming', value: formatBoolean(additional?.DataRoamingEnabled) });
      props.push({ section: 'General Information', sectionIcon: statusIcon, label: 'Personal Hotspot', value: formatBoolean(additional?.PersonalHotspotEnabled) });
      props.push({ section: 'General Information', sectionIcon: statusIcon, label: 'Find My Device', value: formatBoolean(additional?.IsDeviceLocatorServiceEnabled) });
    }
    props.push({ section: 'General Information', sectionIcon: statusIcon, label: 'iTunes Account Active', value: formatBoolean(additional?.iTunesStoreAccountIsActive) });
    props.push({ section: 'General Information', sectionIcon: statusIcon, label: 'Awaiting Configuration', value: formatBoolean(additional?.AwaitingConfiguration) });

    return props;
  }, [device, additional, isMac, isIOS]);

  // Filter properties based on search
  const filteredProperties = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const query = searchQuery.toLowerCase();
    return allProperties.filter(
      prop => prop.label.toLowerCase().includes(query) || prop.value.toLowerCase().includes(query)
    );
  }, [searchQuery, allProperties]);

  // Group filtered properties by section
  const groupedFilteredProperties = useMemo(() => {
    if (!filteredProperties) return null;
    const groups: Record<string, PropertyItem[]> = {};
    filteredProperties.forEach(prop => {
      if (!groups[prop.section]) {
        groups[prop.section] = [];
      }
      groups[prop.section].push(prop);
    });
    return groups;
  }, [filteredProperties]);

  const InfoField = ({ label, value }: { label: string; value: string | null | undefined }) => {
    const displayValue = value || 'Not Reported';
    const isLongValue = displayValue.length > 30;
    
    return (
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        {isLongValue ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="font-medium text-sm text-foreground truncate cursor-help">
                  {displayValue}
                </p>
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs break-all">{displayValue}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <p className="font-medium text-sm text-foreground">{displayValue}</p>
        )}
      </div>
    );
  };

  const isSearching = searchQuery.trim().length > 0;

  return (
    <GlassCard variant="default" hover={false} className="p-6 mx-0">
      <h2 className="text-xl font-semibold mb-4 text-foreground">System Information</h2>
      
      {/* Search Input */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by property name or value..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 pr-9"
        />
        {searchQuery && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
            onClick={() => setSearchQuery('')}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Search Results Count */}
      {isSearching && (
        <p className="text-sm text-muted-foreground mb-4">
          Found {filteredProperties?.length || 0} matching {filteredProperties?.length === 1 ? 'property' : 'properties'}
        </p>
      )}

      {/* Filtered Results View */}
      {isSearching && groupedFilteredProperties && (
        <div className="space-y-4">
          {Object.entries(groupedFilteredProperties).map(([section, props]) => (
            <div key={section} className="border border-border/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                {props[0].sectionIcon}
                <span className="font-semibold text-sm">{section}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {props.map((prop, idx) => (
                  <InfoField key={idx} label={prop.label} value={prop.value} />
                ))}
              </div>
            </div>
          ))}
          {filteredProperties?.length === 0 && (
            <p className="text-center text-muted-foreground py-8">No properties match your search.</p>
          )}
        </div>
      )}

      {/* Normal Accordion View (when not searching) */}
      {!isSearching && (
        <Accordion type="multiple" defaultValue={["identity", "network", "hardware", "system"]} className="w-full">
          {/* Device Identity */}
          <AccordionItem value="identity">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <Smartphone className="h-4 w-4 text-primary" />
                <span className="font-semibold">Device Identity</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3 pt-2 pl-4">
                <InfoField label="Device Name" value={device.name} />
                <InfoField label="Hostname" value={device.hostname} />
                <InfoField label="Serial Number" value={device.serial_number} />
                <InfoField label="UDID" value={additional?.UDID} />
                <InfoField label="Product" value={additional?.ProductName} />
                {additional?.ModelNumber && <InfoField label="Model Number" value={additional.ModelNumber} />}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Network Information */}
          <AccordionItem value="network">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <Network className="h-4 w-4 text-primary" />
                <span className="font-semibold">Network Information</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3 pt-2 pl-4">
                <InfoField label="IP Address" value={device.ip_address} />
                <InfoField label="WiFi MAC Address" value={formatMacAddress(additional?.WiFiMAC)} />
                <InfoField label="Bluetooth MAC Address" value={formatMacAddress(additional?.BluetoothMAC)} />
                {isMac && additional?.EthernetMAC && <InfoField label="Ethernet MAC Address" value={formatMacAddress(additional.EthernetMAC)} />}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Hardware & Storage */}
          <AccordionItem value="hardware">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <HardDrive className="h-4 w-4 text-primary" />
                <span className="font-semibold">Hardware & Storage</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3 pt-2 pl-4">
                <InfoField label="Battery Level" value={formatPercentage(additional?.BatteryLevel || device.battery_level)} />
                <InfoField label="Battery Status" value={device.battery_status} />
                <InfoField label="Total Capacity" value={formatBytes(additional?.DeviceCapacity)} />
                <InfoField label="Available Capacity" value={formatBytes(additional?.AvailableDeviceCapacity)} />
                {additional?.HasBattery !== undefined && <InfoField label="Has Battery" value={formatBoolean(additional.HasBattery)} />}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* System Details */}
          <AccordionItem value="system">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <Monitor className="h-4 w-4 text-primary" />
                <span className="font-semibold">System Details</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3 pt-2 pl-4">
                <InfoField label="Operating System" value={device.os_type} />
                <InfoField label="OS Version" value={device.os_version} />
                <InfoField label="Build Version" value={additional?.BuildVersion} />
                <InfoField label="Hostname" value={device.hostname} />
                {additional?.SupplementalBuildVersion && <InfoField label="Supplemental Build" value={additional.SupplementalBuildVersion} />}
                {isIOS && additional?.TimeZone && <InfoField label="Time Zone" value={additional.TimeZone} />}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Cellular Information (iOS only) */}
          {isIOS && additional?.ServiceSubscriptions && additional.ServiceSubscriptions.length > 0 && (
            <AccordionItem value="cellular">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-2">
                  <Signal className="h-4 w-4 text-primary" />
                  <span className="font-semibold">Cellular Information</span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4 pt-2 pl-4">
                  {additional.ServiceSubscriptions.map((sub, index) => (
                    <div key={index} className="border-b border-border/30 pb-4 last:border-0">
                      <p className="text-xs font-semibold text-muted-foreground mb-2">
                        {sub.Slot?.replace('CTSubscriptionSlot', 'Slot ')}
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {sub.IMEI && <InfoField label="IMEI" value={sub.IMEI} />}
                        {sub.MEID && <InfoField label="MEID" value={sub.MEID} />}
                        {sub.EID && <InfoField label="EID" value={sub.EID} />}
                      </div>
                    </div>
                  ))}
                  {additional.ModemFirmwareVersion && <InfoField label="Modem Firmware" value={additional.ModemFirmwareVersion} />}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}

          {/* User Information (Mac only) */}
          {isMac && (additional?.UserLongName || additional?.UserShortName || additional?.UserID) && (
            <AccordionItem value="user">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-primary" />
                  <span className="font-semibold">User Information</span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3 pt-2 pl-4">
                  <InfoField label="User ID" value={additional?.UserID} />
                  <InfoField label="User Name" value={additional?.UserLongName} />
                  <InfoField label="Short Name" value={additional?.UserShortName} />
                  <InfoField label="On Console" value={formatBoolean(!additional?.NotOnConsole)} />
                </div>
              </AccordionContent>
            </AccordionItem>
          )}

          {/* Status & Policies */}
          <AccordionItem value="status">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <Info className="h-4 w-4 text-primary" />
                <span className="font-semibold">General Information</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3 pt-2 pl-4">
                <InfoField label="Last Seen" value={formatDate(device.last_seen || device.created_at)} />
                <InfoField label="Supervised" value={formatBoolean(additional?.IsSupervised)} />
                {isIOS && (
                  <>
                    <InfoField label="Activation Lock" value={formatBoolean(additional?.IsActivationLockEnabled)} />
                    <InfoField label="Cloud Backup" value={formatBoolean(additional?.IsCloudBackupEnabled)} />
                    <InfoField label="Lost Mode" value={formatBoolean(additional?.IsMDMLostModeEnabled)} />
                    <InfoField label="Data Roaming" value={formatBoolean(additional?.DataRoamingEnabled)} />
                    <InfoField label="Personal Hotspot" value={formatBoolean(additional?.PersonalHotspotEnabled)} />
                    <InfoField label="Find My Device" value={formatBoolean(additional?.IsDeviceLocatorServiceEnabled)} />
                  </>
                )}
                <InfoField label="iTunes Account Active" value={formatBoolean(additional?.iTunesStoreAccountIsActive)} />
                <InfoField label="Awaiting Configuration" value={formatBoolean(additional?.AwaitingConfiguration)} />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
    </GlassCard>
  );
};

export default SystemInformation;
